import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

const CACHE_KEY = "atlas-user-group";

type UserGroup = {
  /** `null` mientras no se conoce (primer login sin cache). */
  isDevTester: boolean | null;
  /** `grupo_testing` de la empresa. `null` mientras no se conoce; tratar como 'A'. */
  grupo: string | null;
};

type CachedUserGroup = { devTester: boolean; grupo: string };

function readCache(): UserGroup {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return { isDevTester: null, grupo: null };
    const parsed = JSON.parse(raw) as CachedUserGroup;
    return { isDevTester: parsed.devTester === true, grupo: parsed.grupo ?? null };
  } catch {
    return { isDevTester: null, grupo: null };
  }
}

/**
 * Lee `dev_tester` y `grupo_testing` de `company_profiles` para el usuario
 * actual. Cachea en localStorage para evitar parpadeo de la UI (el nav cambia
 * según esto). Mientras no se conoce devuelve el último valor cacheado, o
 * `{ null, null }` en el primer login.
 *
 * Es la fuente única de fetch para el gating por grupo; `useDevTester` delega
 * en este hook.
 */
export function useUserGroup(): UserGroup {
  const [state, setState] = useState<UserGroup>(readCache);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        if (!profile?.id_company) return;

        const { data: company } = await supabase
          .from("company_profiles")
          .select("dev_tester, grupo_testing")
          .eq("id_company", profile.id_company)
          .single();
        if (cancelled || !company) return;

        const next: UserGroup = {
          isDevTester: company.dev_tester === true,
          grupo: (company.grupo_testing as string | null) ?? null,
        };
        setState(next);
        try {
          localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ devTester: next.isDevTester, grupo: next.grupo }),
          );
        } catch {
          /* noop */
        }
      } catch {
        /* noop — ante error mantenemos el valor cacheado */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
